// The customer's view of an inspection call.
//
// Deliberately almost empty: video, mute, leave. No checklist, no transcript,
// no cost meter, no settings — none of that is the customer's business, and a
// phone screen has no room for it anyway.
//
// Unauthenticated by design. The call ID in the link is what admits them.

import { useCallback, useEffect, useRef, useState } from 'react';
import { LiveSwitchCall, type Participant } from '../liveswitch/call';
import { CallStage } from './CallStage';
import { MediaControls } from './MediaControls';

type Props = {
  callId: string | null;
};

export function GuestView({ callId }: Props) {
  const [displayName, setDisplayName] = useState('');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [inCall, setInCall] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [swapAsk, setSwapAsk] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);

  const callRef = useRef<LiveSwitchCall | null>(null);

  const join = useCallback(async () => {
    if (!callId || callRef.current?.isJoined || joining) return;
    setError(null);
    setJoining(true);

    const call = new LiveSwitchCall({
      onParticipants: setParticipants,
      onError: (message) => setError(message),
      // A request, not a remote control: the customer is holding the phone and
      // decides whether to turn it round.
      onCameraSwapRequested: (from) => setSwapAsk(from),
      onRecording: setRecording,
    });
    callRef.current = call;

    try {
      await call.join(callId, displayName.trim() || 'Guest');
      setInCall(true);
    } catch (err) {
      callRef.current = null;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setJoining(false);
    }
  }, [callId, displayName, joining]);

  const leave = useCallback(async () => {
    const call = callRef.current;
    callRef.current = null;
    setInCall(false);
    setParticipants([]);
    setSwapAsk(null);
    if (call) await call.leave();
  }, []);

  useEffect(() => {
    const onUnload = () => void callRef.current?.leave();
    window.addEventListener('pagehide', onUnload);
    return () => {
      window.removeEventListener('pagehide', onUnload);
      onUnload();
    };
  }, []);

  if (!callId) {
    return (
      <div className="guest">
        <div className="guest__gate">
          <h1>Longview</h1>
          <p className="guest__lead">
            This page opens a video call with your Longview inspector.
          </p>
          <p className="empty-note">
            You need the link they sent you &mdash; it carries the call it belongs to. Open that
            link on this device and the call will be ready to join.
          </p>
        </div>
      </div>
    );
  }

  if (!inCall) {
    // A tap has to happen before anything starts: iOS will not grant camera and
    // microphone, or play the other side's audio, without a user gesture.
    return (
      <div className="guest">
        <div className="guest__gate">
          <h1>Longview</h1>
          <p className="guest__lead">Your inspector is waiting to see the damage.</p>

          <label className="guest__field">
            <span className="eyebrow">Your name</span>
            <input
              value={displayName}
              placeholder="First name"
              autoComplete="given-name"
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </label>

          <button
            type="button"
            className="guest__join"
            disabled={joining}
            onClick={() => void join()}
          >
            {joining ? 'Connecting…' : 'Join the call'}
          </button>

          {/* Consent sits on the button itself rather than behind a checkbox.
              Placeholder wording - the legal phrasing is still to be settled,
              and recording rules differ by state. */}
          <p className="consent">
            By joining this call you agree to it being recorded, including video and audio.
          </p>

          <p className="empty-note">
            Your phone will ask to use the camera and microphone. Both are needed so the
            inspector can see and hear you.
          </p>

          {error && (
            <p className="guest__error" role="status">
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="guest guest--live">
      <CallStage
        participants={participants}
        inCall
        placeholder="Waiting for your inspector to join…"
        status="Live"
      />

      {/* Visible for the whole call, not just at the point of consent. */}
      {recording && (
        <div className="recbadge" role="status">
          <span className="recbadge__dot" /> Recording
        </div>
      )}

      {swapAsk && (
        <div className="swapask" role="status">
          <span>{swapAsk} asked you to switch cameras.</span>
          <button
            type="button"
            className="swapask__yes"
            onClick={() => {
              setSwapAsk(null);
              // Not `void`: a swap that quietly does nothing is exactly how the
              // no-op bug hid, so a failure says so instead.
              callRef.current
                ?.swapCamera()
                .then((label) => {
                  if (!label) setError('No other camera to switch to.');
                })
                .catch((err) =>
                  setError(err instanceof Error ? err.message : 'Could not switch camera.'),
                );
            }}
          >
            Switch
          </button>
          <button type="button" className="swapask__no" onClick={() => setSwapAsk(null)}>
            Not now
          </button>
        </div>
      )}

      <div className="guest__bar">
        <MediaControls call={callRef.current} compact onLeave={() => void leave()} />
      </div>

      {error && (
        <p className="guest__error" role="status">
          {error}
        </p>
      )}
    </div>
  );
}
